import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Skeleton,
} from "@heroui/react";
import {
  ChevronLeft,
  Download,
  Eye,
  FileText,
  Calendar,
  AlertCircle,
  BookOpen,
  GraduationCap,
  Hash,
  Building2,
  ScrollText,
  Lock,
  LogIn,
} from "lucide-react";
import {
  useGetPascoById,
  useGetPascoPreviewUrl,
  useDownloadPascoStream,
} from "@src/presentation/hooks/pasco";
import { ToastService } from "@src/shared/services/toast.service";
import { formatDateFull, formatDateHuman } from "@src/shared/utils/date-formatter";
import { useAuth } from "@src/presentation/contexts/AuthContext";
import { useAnalytics } from "@src/presentation/hooks/analytics";

export default function PublicPascoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const analytics = useAnalytics();

  // Fetch PASCO details only if authenticated
  const { data: pasco, isLoading, error } = useGetPascoById(id || "", {
    enabled: isAuthenticated, // Only fetch if authenticated
  });

  // Fetch preview URL for JPG preview (auto-fetch once PASCO details are loaded)
  const {
    data: previewUrl,
    isLoading: isLoadingPreview,
  } = useGetPascoPreviewUrl(id, {
    enabled: !!id && !!pasco && isAuthenticated, // Fetch when we have PASCO ID, PASCO details are loaded, and user is authenticated
  });

  // Track pasco view when loaded
  useEffect(() => {
    if (pasco && isAuthenticated) {
      analytics.trackPascoView(pasco.pascoId, `${pasco.courseCode || 'Unknown'} - ${pasco.courseName || 'Untitled'}`, {
        course_code: pasco.courseCode ?? undefined,
        course_name: pasco.courseName ?? undefined,
        department: pasco.department ?? undefined,
        level: pasco.level ?? undefined,
        semester: pasco.semester ?? undefined,
        academic_year: pasco.academicYear ?? undefined,
        is_interim: pasco.isInterimAssessment,
        is_supplementary: pasco.isSupplementary,
        is_reviewed: pasco.isReviewed,
        page: 'public_detail',
      });
    }
  }, [pasco, isAuthenticated, analytics]);

  // Download mutation for streaming the file
  const { mutate: downloadPascoStream, isPending: isDownloading } = useDownloadPascoStream({
    onSuccess: () => {
      ToastService.success("Download started successfully");
    },
    onError: (error) => {
      ToastService.error(`Failed to download PASCO: ${error.message}`);
    }
  });

  // Check authentication FIRST - before trying to load anything
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <Card className="max-w-md w-full shadow-none border border-border rounded">
          <CardBody className="text-center p-8">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full bg-primary/10">
                <Lock className="h-12 w-12 text-primary" />
              </div>
            </div>

            <h2 className="text-xl font-semibold text-foreground mb-2">
              Login Required
            </h2>

            <p className="text-default-500 mb-6">
              This PASCO document requires authentication. Please sign in to view.
            </p>

            <div className="flex flex-col gap-2">
              <Button
                color="primary"
                onPress={() => navigate("/auth", { state: { from: `/pascos/${id}` } })}
                startContent={<LogIn className="h-4 w-4" />}
                className="rounded"
              >
                Login to View
              </Button>
              <Button
                variant="flat"
                onPress={() => navigate("/")}
                className="rounded"
              >
                Back to Home
              </Button>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  // If PASCO not found or loading, show appropriate state
  if (isLoading) {
    return (
      <div className="min-h-screen h-full bg-gradient-to-br from-background via-background to-default-50 overflow-auto">
        {/* Header skeleton */}
        <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-custom">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
              <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
                <Skeleton className="h-8 w-20 rounded" />
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Skeleton className="h-8 w-24 rounded" />
              </div>
            </div>
          </div>
        </header>

        {/* Main content skeleton */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left column - PDF and course info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Course information */}
              {/* <Card className="shadow-none border border-border rounded">
                <CardHeader>
                  <Skeleton className="h-6 w-40" />
                </CardHeader>
                <CardBody>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <Skeleton className="h-4 w-20 mb-2" />
                        <Skeleton className="h-5 w-32" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-5 w-28" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-16 mb-2" />
                        <Skeleton className="h-5 w-24" />
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Skeleton className="h-4 w-18 mb-2" />
                        <Skeleton className="h-5 w-36" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-22 mb-2" />
                        <Skeleton className="h-5 w-30" />
                      </div>
                      <div>
                        <Skeleton className="h-4 w-20 mb-2" />
                        <Skeleton className="h-5 w-26" />
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card> */}

              {/* PDF Preview */}
              <Card className="shadow-none border border-border rounded">
                <CardHeader>
                  <Skeleton className="h-6 w-32" />
                </CardHeader>
                <CardBody>
                  <Skeleton className="h-[calc(100vh-15rem)] w-full rounded" />
                </CardBody>
              </Card>
            </div>

            {/* Right column - Metadata */}
            <div className="space-y-6">
              <Card className="shadow-none border border-border rounded">
                <CardHeader>
                  <Skeleton className="h-6 w-24" />
                </CardHeader>
                <CardBody>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardBody>
              </Card>

              <Card className="shadow-none border border-border rounded">
                <CardHeader>
                  <Skeleton className="h-6 w-24" />
                </CardHeader>
                <CardBody>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="space-y-1">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-16" />
                      </div>
                    </div>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error || !pasco) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md shadow-none border border-border rounded">
          <CardBody className="text-center p-8">
            <AlertCircle className="h-12 w-12 text-danger mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">PASCO Not Found</h2>
            <p className="text-default-500 mb-4">
              {error?.message || "The PASCO you're looking for doesn't exist or has been removed."}
            </p>
            <Button color="primary" onPress={() => navigate("/")} className="rounded">
              Back to Home
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const handleDownload = async () => {
    // Ensure user is authenticated before allowing download
    if (!isAuthenticated) {
      ToastService.error("You must be logged in to download this file.");
      navigate("/auth", { state: { from: `/pascos/${id}` } });
      return;
    }

    if (!id || !pasco) return;

    // Track download action
    analytics.trackPascoClick(id, 'download', {
      pasco_title: `${pasco.courseCode} - ${pasco.courseName}`,
      course_code: pasco.courseCode,
      page: 'public_detail',
    });

    analytics.trackDownload(
      `${pasco.courseCode}_${pasco.semester}_${pasco.academicYear}.pdf`,
      'pdf',
      {
        pasco_id: id,
        course_code: pasco.courseCode,
        source: 'public_detail_page',
      }
    );

    // Generate filename based on PASCO details
    const filename = `${pasco.courseCode}_${pasco.semester}_${pasco.academicYear}.pdf`.replace(/\s+/g, '_');

    // Use the new stream endpoint
    downloadPascoStream({ pascoId: id, filename });
  };

  // Helper function to get status color
  // const getStatusColor = (status: string) => {
  //   switch (status) {
  //     case "completed":
  //       return "success";
  //     case "processing":
  //       return "primary";
  //     case "failed":
  //       return "danger";
  //     case "pending":
  //       return "warning";
  //     default:
  //       return "default";
  //   }
  // };

  // const getStatusIcon = (status: string) => {
  //   switch (status) {
  //     case "completed":
  //       return <CheckCircle className="h-4 w-4" />;
  //     case "processing":
  //       return <Clock className="h-4 w-4" />;
  //     case "failed":
  //       return <XCircle className="h-4 w-4" />;
  //     case "pending":
  //       return <Clock className="h-4 w-4" />;
  //     default:
  //       return null;
  //   }
  // };

  return (
    <div className="min-h-screen h-full bg-gradient-to-br from-background via-background to-default-50 overflow-auto">
      {/* Header with gradient */}
      <header className="border-b border-border bg-background/80 backdrop-blur-xl supports-[backdrop-filter]:bg-background/60 sticky top-0 z-20 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row h-auto sm:h-16 py-3 sm:py-0 items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div className="flex items-start sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <Button
                variant="light"
                size="sm"
                onPress={() => navigate("/")}
                startContent={<ChevronLeft className="h-4 w-4" />}
                className="hover:scale-105 transition-transform"
              >
                Back
              </Button>
              <Divider orientation="vertical" className="h-6 hidden sm:block" />
              <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                  <FileText className="h-4 sm:h-5 w-4 sm:w-5 text-primary-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="text-sm sm:text-xl font-semibold text-foreground truncate">
                    {pasco.courseCode} - {pasco.courseName}
                  </h1>
                  <p className="text-xs text-default-500">{pasco.academicYear}</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button
                variant="flat"
                color="default"
                onPress={handleDownload}
                startContent={<Download className="h-3 sm:h-4 w-3 sm:w-4" />}
                className="rounded flex-1 sm:flex-initial"
                isLoading={isDownloading}
                size="sm"
              >
                <span className="hidden sm:inline">Download PDF</span>
                <span className="sm:hidden">Download</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - PDF Viewer */}
          <div className="lg:col-span-2 space-y-6">
            <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between border-b border-divider pb-4">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Eye className="h-5 w-5 text-primary" />
                  </div>
                  <h2 className="text-lg font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                    PDF Preview
                  </h2>
                </div>
              </CardHeader>
              <CardBody className="p-0">
                <div className="bg-default-50 rounded-b-xl h-max md:h-[calc(100vh-250px)] overflow-y-hidden overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-default-100 [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-default-400">
                  {isLoadingPreview ? (
                    <div className="w-full h-full rounded p-4">
                      <Skeleton className="w-full h-full rounded" />
                    </div>
                  ) : previewUrl ? (
                    <img
                      src={previewUrl.previewUrl}
                      alt={`${pasco.courseCode} Preview`}
                      className="w-full h-auto object-contain"
                    />
                  ) : (
                    <div className="w-full h-full rounded flex items-center justify-center">
                      <div className="text-center">
                        <AlertCircle className="h-12 w-12 text-warning mx-auto mb-4" />
                        <p className="text-default-600">
                          Unable to load preview. Please use the Download button above.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Right Column - Metadata */}
          <div className="space-y-6 h-full overflow-y-auto pb-18 lg:pb-0 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-default-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-default-400 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-default-300 hover:scrollbar-thumb-default-400">
            {/* Status Card */}
            {/* <Card className="shadow-none border border-border rounded">
              <CardHeader className="border-b border-divider pb-3">
                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Status
                </h3>
              </CardHeader>
              <CardBody className="space-y-3 flex md:flex-row flex:justify-between gap-6">
                <div>
                  <p className="text-xs text-default-500 mb-1.5 uppercase tracking-wide font-medium">
                    Processing Status
                  </p>
                  <Chip
                    color={getStatusColor(pasco.processingStatus)}
                    variant="flat"
                    startContent={getStatusIcon(pasco.processingStatus)}
                    className="shadow-sm"
                  >
                    {pasco.processingStatus.charAt(0).toUpperCase() + pasco.processingStatus.slice(1)}
                  </Chip>
                </div>
                <div>
                  <p className="text-xs text-default-500 mb-1.5 uppercase tracking-wide font-medium">
                    Approval Status
                  </p>
                  <Chip
                    color={pasco.isApproved ? "success" : "warning"}
                    variant="flat"
                    startContent={
                      pasco.isApproved ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <Clock className="h-4 w-4" />
                      )
                    }
                    className="shadow-sm"
                  >
                    {pasco.isApproved ? "Approved" : "Pending Approval"}
                  </Chip>
                </div>
              </CardBody>
            </Card> */}

            {/* Course Information */}
            <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
              <CardHeader className="border-b border-divider pb-3">
                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Course Information
                </h3>
              </CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <BookOpen className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Code</p>
                    <p className="text-sm font-semibold text-foreground">{pasco.courseCode}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <FileText className="h-4 w-4 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Course Name</p>
                    <p className="text-sm font-semibold text-foreground">{pasco.courseName}</p>
                  </div>
                </div>
                {pasco.department && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-warning/10">
                      <Building2 className="h-4 w-4 text-warning" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Department</p>
                      <p className="text-sm font-semibold text-foreground">{pasco.department}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-success/10">
                    <GraduationCap className="h-4 w-4 text-success" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Level</p>
                    <p className="text-sm font-semibold text-foreground">Level {pasco.level}</p>
                  </div>
                </div>
                {pasco.courseDescription && (
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <ScrollText className="h-4 w-4 text-primary" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-default-500 uppercase tracking-wide font-medium">
                        Course Description
                      </p>
                      <p className="text-sm text-foreground leading-relaxed mt-1">
                        {pasco.courseDescription}
                      </p>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>

            {/* Academic Details */}
            <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
              <CardHeader className="border-b border-divider pb-3">
                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Academic Details
                </h3>
              </CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Calendar className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Academic Year</p>
                    <p className="text-sm font-semibold text-foreground">{pasco.academicYear}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-secondary/10">
                    <Hash className="h-4 w-4 text-secondary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Semester</p>
                    <p className="text-sm font-semibold text-foreground">
                      {pasco.semester === 1 ? "First Semester" : "Second Semester"}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-success/10">
                    <Calendar className="h-4 w-4 text-success" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-default-500 uppercase tracking-wide font-medium">Created Date</p>
                    <p className="text-sm font-semibold text-foreground">{formatDateFull(pasco.createdAt)}</p>
                    <p className="text-xs text-default-400 mt-0.5">{formatDateHuman(pasco.createdAt)}</p>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Uploaded By User */}
            {/* <Card className="shadow-none border border-border rounded hover:shadow-custom transition-shadow">
              <CardHeader className="border-b border-divider pb-3">
                <h3 className="text-sm font-semibold bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  Uploaded By
                </h3>
              </CardHeader>
              <CardBody className="space-y-3">
                <div className="flex items-start gap-3">
                  {pasco.uploadedBy ? (
                    <>
                      {pasco.uploadedBy.profilePicture ? (
                        <Avatar
                          src={pasco.uploadedBy.profilePicture}
                          name={pasco.uploadedBy.name}
                          size="md"
                          className="flex-shrink-0"
                        />
                      ) : (
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-600 shadow-md flex-shrink-0">
                          <User className="h-5 w-5 text-primary-foreground" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate">
                          {pasco.uploadedBy.name}
                        </p>
                        <p className="text-xs text-default-500 mt-0.5">
                          Uploaded {formatDateHuman(pasco.createdAt)}
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-default-500">
                      <User className="h-5 w-5" />
                      <span className="text-sm">Unknown uploader</span>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card> */}

          </div>
        </div>
      </main>
    </div>
  );
}
